import { useMemo } from "react";
import { Image } from "@ludens-reklame/rubics-v3-sdk/file/types";
import type { BackgroundColor } from "../../types/theme.js";
import { transformImageUrl } from "@ludens-reklame/rubics-v3-sdk/file/utils";
import { ButtonAction, ButtonActionProps } from "../../internal-components/Button.js";
import { RichText } from "../../internal-components/RichText.js";
import { Constrainer } from "../../internal-components/Layout.js";
import {
  Layout,
  Title,
  Background,
  Content,
  Buttons,
  HorizontalSpacer,
  HeroImageWrapper,
  ContentPanel,
  HeroImg,
} from "./styles.js";

export interface HeroSlideProps {
  title?: string;
  titleSize?: number;
  text?: string;
  image?: Image;
  mobileImage?: Image;
  backgroundColor?: BackgroundColor;
  actions?: ButtonActionProps[];
}

export const HeroSlide: React.FunctionComponent<HeroSlideProps> = ({
  title,
  titleSize,
  text,
  image,
  mobileImage,
  backgroundColor,
  actions,
}) => {
  const activeActions = useMemo(() => {
    if (!actions) return [];
    return actions.filter((x) => x.link);
  }, [actions]);

  const hasImage = !!image;

  const desktopImg = hasImage
    ? transformImageUrl(image, { width: 1920 })
    : undefined;

  const mobileImg = mobileImage
    ? transformImageUrl(mobileImage, { width: 960 })
    : undefined;

  if (!title && !text && !hasImage) {
    return null;
  }

  if (hasImage) {
    return (
      <Constrainer>
        <HeroImageWrapper>
          <picture>
            {mobileImg && (
              <source media="(max-width: 959px)" srcSet={mobileImg} />
            )}
            <HeroImg src={desktopImg} alt={title || ""} />
          </picture>
          <ContentPanel $bg={backgroundColor}>
            {title && (
              <h1 style={titleSize ? { fontSize: `${titleSize}px` } : undefined}>
                {title}
              </h1>
            )}
            {text && (
              <RichText
                className="rich-text b2"
                dangerouslySetInnerHTML={{ __html: text }}
              />
            )}
            {activeActions.length > 0 && (
              <Buttons $hasImage>
                {activeActions.map((action, i) => (
                  <ButtonAction key={i} {...action} />
                ))}
              </Buttons>
            )}
          </ContentPanel>
        </HeroImageWrapper>
      </Constrainer>
    );
  }

  return (
    <Constrainer>
      <Background $bg={backgroundColor}>
        <HorizontalSpacer>
          <Layout $hasImage={false} $hasSlides={false}>
            <Content $hasImage={false} $hasSlides={false}>
              {title && <Title $size={titleSize}>{title}</Title>}
              {text && (
                <RichText
                  className="rich-text b2"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              )}
              {activeActions.length > 0 && (
                <Buttons className="hero--actions" $hasImage={false}>
                  {activeActions.map((action, i) => (
                    <ButtonAction key={i} {...action} />
                  ))}
                </Buttons>
              )}
            </Content>
          </Layout>
        </HorizontalSpacer>
      </Background>
    </Constrainer>
  );
};
